import React from "react";
import debounce from "lodash/debounce";
import Collapse from "react-collapse";
import asPage from "components/core/as-page";
import Breadcrumbs from "components/core/breadcrumbs";
import Header from "components/core/header";
import HeaderBanner from "components/core/header-banner";
import Layout from "components/core/layout";
import Link from "components/elements/link";
import PhoneLink from "components/elements/phone-link";
import Slider from "components/elements/slider";
import SVG from "components/elements/svg";
import Tabs from "components/elements/tabs";
import TextContent from "components/elements/text-content";
import Video from "components/elements/video";
import CTASignUp from "components/partials/cta-sign-up";
import Integrations from "components/partials/integrations";

const data = [
  {
    icon: "home-laptop",
    title: "Create account",
    content: (
      <p>
        Create a buyer account and follow our easy step by step verification
        process. Please note it can take up to 24 hours before your account is
        activated.
      </p>
    ),
  },
  {
    icon: "properties-page",
    title: "Find property",
    content: <p>Use the search function under the properties section.</p>,
  },
  {
    icon: "doors",
    title: "Book your place on an open day",
    content: (
      <p>
        You must book an appointment online otherwise attendance will be
        refused. Once you have attended you will be able to bid if you wish.
      </p>
    ),
  },
  {
    icon: "our-way-2",
    title: "Bid",
    content: (
      <p>
        Bidding will be restricted to those that have attended an open day 
        and provided any additional infromation that we have requested. 
        Bidding is allowed during the time that the bidding window is open, 
        please check the listing for the scheduled opening and closing times. 
        The closing time is subject to anti-sniping rules (as documented in 
        our Buyer FAQ’s and Buyer terms).
      </p>
    ),
  },
  {
    icon: "reasons-2",
    title: "Win",
    content: (
      <p>
        If your bid is the highest at the time the auction closes, then so long
        as you have met the reserve price you will be announced as the winner.
      </p>
    ),
  },
  {
    icon: "our-way-1",
    title:
      "Pay the Reservation Fee & complete the Proof of Funds Questionnaire.",
    content: (
      <p>
        You must make payment of our Reservation Fee within 2 hours of the
        auction closing (By Bank Transfer, We do not take card payments). Our
        fee is 3.6% inc. VAT (min of £3,600 inc. VAT). This is additional to
        what you will pay the vendor for the property. On receipt you will be
        issued with a VAT receipt and asked to complete the proof of funds
        questionnaire that will be attached to the emailed receipt.
      </p>
    ),
  },
  {
    icon: "our-way-1",
    title: "Complete the Reservation Form",
    content: (
      <p>
        You have 48 hours from the close of the auction to submit your
        Reservation Form which includes your buyer and solicitor details. This
        can be accessed via the link we send you when you are confirmed as the
        winner or you can access it by logging into your account, and looking at
        the notification section within “My Account”.
      </p>
    ),
  },

  {
    icon: "sold",
    title: "Up to 90 days to complete",
    content: (
      <p>
        Our standard terms are that you have 90 days to complete from the close
        of the reservation period. Please check the completion terms on the
        listing and be aware of any special conditions that do apply. There is
        no deposit payable to the vendor until contracts exchange (no deposit
        payable on winning the auction).
      </p>
    ),
  },
];

class HowItWorksBuyer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      collapseSecondText: false,
      showSlider: false,
    };
    this.showSlider = debounce(this.showSlider.bind(this), 10);
  }

  componentDidMount() {
    this.showSlider();
    window.addEventListener("resize", this.showSlider);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.showSlider);
  }

  showSlider() {
    this.setState({ showSlider: window.innerWidth <= 768 });
  }

  changeSlide(interval) {
    this.slider.changeSlide(interval);
  }

  render() {
    const { collapseSecondText } = this.state;
    const { user } = this.props;

    return (
      <Layout className="how-it-works-buyer" title="How it works - Buyer">
        <Header>
          <HeaderBanner
            title="How it works - Buyer"
            icon="home"
            background="/static/images/header-how-it-works.jpg"
          />
        </Header>

        <Breadcrumbs>
          <Breadcrumbs.Item title="How it works" url="how-it-works" />
          <Breadcrumbs.Item title="Buyer" url="how-it-works/buyer" />
        </Breadcrumbs>

        <section className="how-it-works__intro">
          <div className="container">
            <div className="row">
              <div className="col-sm-6 col-intro">
                <h2 className="section-heading">Our fee promise.</h2>
                <TextContent>
                  <ul>
                    <li>
                      It is completely free to register with Hammer Price Homes.
                    </li>
                    <li>
                      We charge the buyer a reservation fee of 3.6% inc. VAT
                      upon winning the auction (subject to a minimum fee of
                      £3,600 inc. VAT) there is nothing payable prior to this.
                    </li>
                    <li>No hidden fees guaranteed.</li>
                  </ul>
                </TextContent>
              </div>

              <div className="col-sm-6">
                <h2 className="section-heading">
                  Your cash is safe - guaranteed.
                </h2>
                <TextContent>
                  <p>
                    We work in partnership with NatWest to ensure that your
                    payments are made securely and that they are held in our
                    client bank account until contracts have exchanged between
                    you and the seller.
                  </p>
                  <p>
                    Once this has happened we will invoice you and the fee is
                    released to Hammer Price Homes. The money remains yours
                    until this time.&nbsp;
                    {!collapseSecondText && (
                      <button
                        type="button"
                        className="text-orange text-medium"
                        onClick={() =>
                          this.setState({ collapseSecondText: true })
                        }
                      >
                        Read more{" "}
                        <SVG src="chevron-down" className="icon-chevron" />
                      </button>
                    )}
                  </p>
                  <Collapse isOpened={collapseSecondText}>
                    <p>
                      As listed in our Buyer Terms & Conditions there are some
                      circumstances whereby you will be eligible for a refund of
                      your reservation fee. Under such circumstances we will
                      return your money no quibble and promptly.
                    </p>
                    <p>
                      Please read the&nbsp;
                      <a
                        href="/static/files/buyer-terms.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Buyer Terms & Conditions
                      </a>
                      &nbsp;and our <Link to="faq#buyer">Buyer FAQ's</Link> for
                      more information.
                    </p>
                    <button
                      type="button"
                      className="text-orange text-medium"
                      onClick={() =>
                        this.setState({ collapseSecondText: false })
                      }
                    >
                      Show less{" "}
                      <SVG src="chevron-up" className="icon-chevron" />
                    </button>
                  </Collapse>
                </TextContent>
              </div>
            </div>
          </div>
        </section>

        <section className="how-it-works__details">
          <div className="container">
            <div className="row">
              {!this.state.showSlider && (
                <div className="col-md-8">
                  {data.map((item, index) => (
                    <div className="how-it-works__point">
                      <div className="image">
                        <SVG src={item.icon} className="icon" />
                      </div>
                      <div className="content">
                        <p className="section-heading">
                          <span className="number">{index + 1}</span>
                          {item.title}
                        </p>

                        <TextContent>{item.content}</TextContent>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {this.state.showSlider && (
                <div className="col-xs-12">
                  <button
                    type="button"
                    onClick={() => this.changeSlide(-1)}
                    className="arrow arrow-left"
                  >
                    <SVG src="chevron-left" className="icon" />
                  </button>
                  <Slider
                    infinite
                    autoplay
                    ref={(el) => {
                      this.slider = el;
                    }}
                  >
                    {data.map((item, index) => (
                      <div className="how-it-works__point" key={item.title}>
                        <div className="image">
                          <SVG src={item.icon} className="icon" />
                        </div>
                        <div className="content">
                          <p className="section-heading">
                            <span className="number">{index + 1}</span>
                            {item.title}
                          </p>

                          <TextContent>{item.content}</TextContent>
                        </div>
                      </div>
                    ))}
                  </Slider>
                  <button
                    type="button"
                    onClick={() => this.changeSlide(1)}
                    className="arrow arrow-right"
                  >
                    <SVG src="chevron-right" className="icon" />
                  </button>
                </div>
              )}
              <div className="col-md-4 col-xs-12">
                <div className="how-it-works__sidebar__item video-item">
                  <p className="section-heading">
                    Buyer process video{" "}
                    <SVG src="chevron-down" className="icon" />
                  </p>

                  <Video
                    title="Buyer process video"
                    url="/static/uploads/buyer-video.mp4"
                    poster="/static/uploads/buyer-video-poster.jpg"
                  />
                </div>

                <div className="how-it-works__sidebar__item">
                  <div className="how-it-works__lead how-it-works__lead--blue">
                    <p className="title">Have any questions?</p>

                    <p className="desc">
                      Proceed to <Link to="faq/buyer">Buyer's FAQ</Link> and
                      learn more about the process.
                    </p>

                    <div className="icon">
                      <img
                        src="/static/images/icon-question-mark.png"
                        className="img-responsive"
                        alt="Have any questions?"
                      />
                    </div>
                  </div>

                  <div className="how-it-works__lead how-it-works__lead--green">
                    <p className="title">Interested in selling?</p>

                    <p className="desc">
                      Check out{" "}
                      <Link to="how-it-works/seller">seller instructions</Link>{" "}
                      and create an account.
                    </p>

                    <div className="icon">
                      <img
                        src="/static/images/icon-i.png"
                        className="img-responsive"
                        alt="Interested in selling?"
                      />
                    </div>
                  </div>
                </div>

                <div className="how-it-works__sidebar__item informations-item">
                  <p className="section-heading">
                    Useful information{" "}
                    <SVG src="chevron-down" className="icon" />
                  </p>

                  <div className="how-it-works__sidebar__tabs">
                    <div className="how-it-works__sidebar__tabs__items">
                      <Tabs.Tab className="tab-item" name="tab-1" default>
                        Proof of funds
                      </Tabs.Tab>

                      <Tabs.Tab className="tab-item" name="tab-2">
                        Anti sniping tool
                      </Tabs.Tab>

                      <Tabs.Tab className="tab-item" name="tab-3">
                        Anti gazumping
                      </Tabs.Tab>
                    </div>

                    <Tabs.TabContent
                      className="tab-content"
                      name="tab-1"
                      default
                    >
                      <TextContent>
                        <p>
                          To satisfy Anti Money Laundering (AML) regulations we
                          must ascertain from winning bidders how they have
                          financed the payment of the reservation fee paid to us
                          as well as how they are going to finance the purchase
                          of the property. Supporting evidence, that shows the
                          source of funds will have to be provided.
                        </p>

                        <p>
                          Within 48 hours of the auction closing the winning
                          bidder must complete the Proof of Funds questionnaire
                          that they receive from us (issued with the VAT receipt
                          for the Reservation Fee payment) and return to us at{" "}
                          <a href="mailto:info@hammerpricehomes.co.uk">
                            info@hammerpricehomes.co.uk
                          </a>
                          .
                        </p>

                        <p>
                          A copy of the form can be viewed/downloaded by
                          clicking on the pdf below. Failure to supply the
                          completed form on time may hold up the issue of the
                          memorandum of sale.
                        </p>

                        <p>
                          <a
                            href="/static/files/proof-of-funds-v2.pdf"
                            target="_blank"
                          >
                            Click here to view/download PDF
                          </a>
                        </p>
                              
                        <p>
                          We may ask for proof of funds prior to bidding,
                          particularly if the property is restricted to 
                          cash buyers only.
                        </p>
                      </TextContent>
                    </Tabs.TabContent>

                    <Tabs.TabContent className="tab-content" name="tab-2">
                      <TextContent>
                        <p>
                          When a bid is placed in the closing minutes of an
                          auction it can prevent serious buyers missing out on a
                          purchase.
                        </p>

                        <p>
                          This practice of 'sniping' leaves both buyers and
                          sellers unsatisfied. For this reason we have developed
                          an anti-sniping tool and incorporated it into our
                          website.
                        </p>

                        <p>
                          If a bid is placed during the last 5 minutes of an
                          auction, the deadline will automatically be reset to 5
                          minutes, giving other bidders time to react. This will
                          continue until no further bids are placed.
                        </p>

                        <p>
                          Auctions often extend due to bids being placed during
                          anti sniping time. Please note that <b>new</b> bidders
                          cannot join an auction after the original scheduled
                          finish time.
                        </p>
                      </TextContent>
                    </Tabs.TabContent>

                    <Tabs.TabContent className="tab-content" name="tab-3">
                      <TextContent>
                        <p>
                          Our process closes on the final bid so won't allow for
                          gazumping. We will issue a 'Memorandum of Sale' once
                          our fee is received which is proof that a sale has
                          been agreed.
                        </p>

                        <p>
                          Although the seller isn't legally bound to sell by
                          this document, our T&C's mean that should a seller
                          give back word then they will incur a financial
                          penalty which has proven to significantly reduce the
                          risk of a sale that’s been agreed falling through.
                        </p>

                        <p>
                          Over 95% of all winning bids have been honoured by
                          Hammer Price Homes sellers.
                        </p>
                      </TextContent>
                    </Tabs.TabContent>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {!user.id && <CTASignUp type="buyer" />}
        <Integrations />
      </Layout>
    );
  }
}

export default asPage(HowItWorksBuyer);
